import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  SnackbarCloseReason,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  alpha
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  LinhasEsqueletoTabela,
  ListagemBuscaField,
  OrdenarPorControl,
  paperListagemSx,
  tableContainerListagemSx,
} from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce, useListagemAltura, useListagemKeyboardNav } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { IListagemTransportadoras, TransportadoraService } from '../../../shared/services/api/cadastros/TransportadoraService';
import { brand } from '../../../shared/themes';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular } from '../../../shared/components/listagem-mobile';

const OPCOES_POR_PAGINA = [8, 25, 50, 100];

export const ListagemDeTransportadoras: React.FC = () => {
  const listaCelular = useCelular();
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const theme = useTheme();
  // No celular esconde CPF/CNPJ e Fantasia (secundárias) em vez de deixar a tabela
  // rolar na horizontal — cabe tudo na tela só com Nome/Status.
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState<IListagemTransportadoras[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'transportadoras';

  const { tabelaRef, containerHeight, rowsPerPage, setRowsPerPage } = useListagemAltura({
    cacheKey: ROWS_CACHE_KEY,
    valorInicial: Environment.LIMITE_DE_CADASTRO,
    rowHeight: 45,
  });

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };

  // Extrai os parâmetros de busca
  const tipo = useMemo(() => searchParams.get('tipo') || 'nome', [searchParams]);
  const filter = useMemo(() => searchParams.get('filter') || '', [searchParams]);
  const pagina = useMemo(() => Number(searchParams.get('pagina') || '1'), [searchParams]);

  useEffect(() => {
    setIsLoading(true);

    debounce(() => {
      TransportadoraService.getAll(pagina, tipo, filter, rowsPerPage)
        .then((result) => {
          setIsLoading(false);

          if (result instanceof Error) {
            setMsgType('error');
            setMsg(result.message);
            setOpenMsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        })
        .catch(() => {
          setIsLoading(false);
          setMsgType('error');
          setMsg('Erro ao carregar dados. Tente novamente mais tarde.');
          setOpenMsg(true);
        });
    });
  }, [pagina, filter, tipo, rowsPerPage, debounce]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ filter, tipo, pagina: '1' }, { replace: true });
    }
  }, [rowsPerPage]);

  // Função para formatar CPF/CNPJ
  const formatarDocumento = (documento: string) => {
    if (!documento) return '';

    const apenasNumeros = documento.replace(/\D/g, '');

    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    return documento;
  };

  const dadosSelecao = [
    { nameBD: 'nome', descricao: 'Nome / Razão Social (F8)', placeholder: 'Pesquisar por nome ou razão social...' },
    { nameBD: 'cpf', descricao: 'CPF / CNPJ (F8)', placeholder: 'Pesquisar por CPF ou CNPJ...' },
    { nameBD: 'fantasia', descricao: 'Apelido / Fantasia (F8)', placeholder: 'Pesquisar por apelido ou nome fantasia...' },
  ];
  const placeholderPesquisa = dadosSelecao.find(item => item.nameBD === tipo)?.placeholder ?? 'Pesquisar transportadoras...';

  const primeiroItem = totalCount === 0 ? 0 : (pagina - 1) * rowsPerPage + 1;
  const ultimoItem = Math.min(pagina * rowsPerPage, totalCount);
  const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  const { pesquisaRef, linhaRefCallback, onBuscaKeyDown, onLinhaKeyDown } = useListagemKeyboardNav({
    rowsLength: rows.length,
    pagina,
    totalPaginas,
    onIrParaPagina: (novaPagina) => setSearchParams({ filter, tipo, pagina: novaPagina.toString() }, { replace: true }),
    onEnter: (index) => navigate(`/transportadoras/${rows[index].idtransportadora}`),
    tiposBusca: dadosSelecao.map((item) => item.nameBD),
    tipoAtual: tipo,
    onTrocarTipo: (novoTipo) => setSearchParams({ filter, tipo: novoTipo, pagina: '1' }, { replace: true }),
    rowsDep: [rows],
  });

  return (
    <LayoutBaseDePagina
      titulo='Transportadoras'
      subtitulo='Visualize, pesquise e gerencie todas as transportadoras cadastradas no sistema.'
      acao={
        <Button
          variant='contained'
          disableElevation
          startIcon={<AddCircleOutlineRoundedIcon />}
          onClick={() => navigate('/transportadoras/novo')}
          sx={{
            height: 44,
            borderRadius: '10px',
            px: 2.5,
            fontWeight: 600,
            bgcolor: brand.menuBase,
            color: '#fff',
            '&:hover': { bgcolor: '#012D3D' },
          }}
        >
          Nova Transportadora
        </Button>
      }
      barraDeFerramentas={
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mx: 1, mb: 2.5 }}>
          <OrdenarPorControl
            opcoes={dadosSelecao}
            valorSelecionado={tipo}
            onChange={(novoTipo) => setSearchParams({ filter, tipo: novoTipo, pagina: '1' }, { replace: true })}
          />

          <ListagemBuscaField
            value={filter}
            onChange={(valor) => setSearchParams({ filter: valor, tipo, pagina: '1' }, { replace: true })}
            placeholder={placeholderPesquisa}
            inputRef={pesquisaRef}
            onKeyDown={onBuscaKeyDown}
          />
        </Box>
      }
    >
      <Snackbar open={openMsg} autoHideDuration={msgType === 'error' ? null : 6000} onClose={handleCloseMsg}>
        <Alert severity={msgType} variant='filled'>{msg}</Alert>
      </Snackbar>

      <Paper elevation={0} sx={paperListagemSx(theme)}>
        <TableContainer
          ref={tabelaRef}
          sx={tableContainerListagemSx(containerHeight, isLoading || rows.length >= rowsPerPage)}
        >
          {/* No desktop, minWidth (não width:'100%') evita que tableLayout:'fixed'
              espreme as colunas abaixo do legível — quem rola é o TableContainer. No
              celular (smDown) CPF/CNPJ e Fantasia somem (ver abaixo), então
              width:'100%' sozinho cabe as colunas restantes na tela. */}
          <Table size="small" sx={{ width: '100%', minWidth: smDown ? 0 : 700, tableLayout: 'fixed' }}>
            {!listaCelular && (
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: smDown ? undefined : '32%' }}>Nome/Razão Social</TableCell>
                {!smDown && <TableCell sx={{ width: '18%' }}>CPF/CNPJ</TableCell>}
                {!smDown && <TableCell sx={{ width: '30%' }}>Nome Fantasia / Apelido</TableCell>}
                <TableCell align='center' sx={{ width: smDown ? '84px' : '20%' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            )}

            {isLoading && rows.length === 0 && (
              <TableBody>
                <LinhasEsqueletoTabela
                  colunas={smDown ? [
                    { width: '85%' },
                    { align: 'center', variant: 'rounded', width: 50, height: 22 },
                  ] : [
                    { width: '85%' },
                    { width: '70%' },
                    { width: '75%' },
                    { align: 'center', variant: 'rounded', width: 50, height: 22 },
                  ]}
                />
              </TableBody>
            )}
            <TableBody>
              {listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={rows}
                  chave={(row) => row.idtransportadora}
                  renderizar={(row) => (
                    <LinhaListagemMobile
                      avatar={row.nome || '?'}
                      titulo={row.nome}
                      detalhes={[formatarDocumento(row.cpf), row.fantasia]}
                      onClick={() => navigate(`/transportadoras/${row.idtransportadora}`)}
                    />
                  )}
                />
              </CelulaListaMobile>
            ) : rows.map((row, index) => (
                <TableRow
                  key={row.idtransportadora}
                  ref={linhaRefCallback(index)}
                  hover
                  tabIndex={0}
                  onClick={() => navigate(`/transportadoras/${row.idtransportadora}`)}
                  onKeyDown={onLinhaKeyDown(index)}
                  sx={{
                    cursor: 'pointer',
                    outline: 'none',
                    '&:last-child td': { borderBottom: 0 },
                    '& td': {
                      py: smDown ? 0.75 : 1.25,
                      px: smDown ? 0.75 : 1.5,
                      fontSize: smDown ? '0.75rem' : '0.8125rem',
                      borderBottom: `1px solid ${theme.torq.border.default}`,
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.035),
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${theme.torq.border.focus}`,
                      outlineOffset: '-2px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <TableCell>
                    <Typography fontWeight={700} fontSize="0.8125rem" color={theme.torq.text.primary} noWrap>
                      {row.nome}
                    </Typography>
                  </TableCell>

                  {!smDown && (
                    <TableCell>
                      <Typography fontWeight={600} fontFamily="monospace" fontSize="0.8125rem" color={theme.torq.text.primary} noWrap>
                        {formatarDocumento(row.cpf)}
                      </Typography>
                    </TableCell>
                  )}

                  {!smDown && (
                    <TableCell>
                      <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                        {row.fantasia || '—'}
                      </Typography>
                    </TableCell>
                  )}

                  <TableCell align="center">
                    <Chip
                      label="Ativo"
                      size="small"
                      sx={{
                        height: '22px',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 1.25 },
                        bgcolor: alpha(theme.palette.success.main, 0.12),
                        color: 'success.main',
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            {rows.length === 0 && !isLoading && (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={smDown ? 2 : 4} align='center' sx={{ py: 3 }}>
                    <Box>
                      <LocalShippingRoundedIcon sx={{
                        fontSize: 35,
                        color: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.text.secondary, 0.4)
                          : 'text.secondary',
                        mb: 1
                      }} />
                      <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                        {Environment.LISTAGEM_VAZIA}
                      </Typography>
                      <Typography variant='body2' color='text.secondary' fontSize="0.7rem">
                        {filter ? 'Tente ajustar os filtros de busca' : 'Clique em "Nova Transportadora" para começar'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={smDown ? 2 : 4} sx={{ p: 0 }}>
                    <LinearProgress sx={{ height: '1.5px' }} />
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={smDown ? 2 : 4} sx={{ py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
                  <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1.5}>
                    <Typography variant='caption' color='text.secondary'>
                      {totalCount === 0
                        ? 'Nenhuma transportadora encontrada'
                        : `Mostrando de ${primeiroItem} a ${ultimoItem} de ${totalCount} transportadora${totalCount !== 1 ? 's' : ''}`}
                    </Typography>

                    {totalCount > rowsPerPage && (
                      <Pagination
                        page={pagina}
                        count={totalPaginas}
                        onChange={(_, newPage) => setSearchParams({ filter, tipo, pagina: newPage.toString() }, { replace: true })}
                        size="small"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        boundaryCount={1}
                        sx={{
                          '& .MuiPaginationItem-root.Mui-selected': {
                            backgroundColor: brand.menuBase,
                            color: '#fff',
                            '&:hover': { backgroundColor: '#012D3D' },
                          },
                        }}
                      />
                    )}

                    <FormControl size='small' sx={{ minWidth: 110 }}>
                      <Select<number> value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                        {(OPCOES_POR_PAGINA.includes(rowsPerPage) ? OPCOES_POR_PAGINA : [...OPCOES_POR_PAGINA, rowsPerPage].sort((a, b) => a - b)).map((opcao) => (
                          <MenuItem key={opcao} value={opcao}>{opcao} / página</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </LayoutBaseDePagina>
  );
};
